// Read video files that are stored (not compressed) inside classic multi-volume RAR archives,
// without ever fully unpacking them - the common shape of scene releases, where the video itself
// is RAR "method 0x30 / store" (compressing an already-compressed x264/x265 stream buys nothing)
// split across a numbered set of volumes (`release.rar` + `.r00/.r01/...`, or the newer
// `release.part01.rar/.part02.rar/...` naming).
//
// For a stored entry, a volume's file-data block is just a verbatim byte range of the original
// file; the full file is simply the concatenation, in volume order, of every chunk that carries
// its name. So we only ever need to parse the (tiny) RAR block headers - never decompress - to
// build a map of "virtual offset in the unpacked file" -> "byte range in one physical volume",
// and then reuse the existing piece-aware torrent byte reader per chunk.
//
// Only the "old" RAR 1.5-4.x header format is supported (still what most scene groups use for
// huge/older releases). RAR5 archives and genuinely compressed entries are recognized and
// reported as unsupported rather than guessed at.

export const METHOD_STORE = 0x30;

const MARKER_OLD = Uint8Array.of(0x52, 0x61, 0x72, 0x21, 0x1a, 0x07, 0x00);
const MARKER_RAR5 = Uint8Array.of(0x52, 0x61, 0x72, 0x21, 0x1a, 0x07, 0x01, 0x00);

const HEAD_MARK = 0x72;
const HEAD_MAIN = 0x73;
const HEAD_FILE = 0x74;
const HEAD_COMM = 0x75;
const HEAD_NEWSUB = 0x7a;
const HEAD_ENDARC = 0x7b;

const LHD_SPLIT_BEFORE = 0x0001;
const LHD_SPLIT_AFTER = 0x0002;
const LHD_PASSWORD = 0x0004;
const LHD_LARGE = 0x0100;
const LHD_UNICODE = 0x0200;
const LHD_SALT = 0x0400;
const LHD_EXTTIME = 0x1000;
const LHD_WINDOWMASK = 0x00e0;
const LHD_WINDOW_DIR = 0x00e0; // all 3 window bits set => directory entry
const LONG_BLOCK = 0x8000;

export class RarFormatError extends Error {
  status = 422;
}
/** Recognized but deliberately not handled: RAR5, encrypted or genuinely compressed entries. */
export class RarUnsupportedError extends Error {
  status = 422;
}

export interface RarChunk {
  /** index into the volumes array passed to readRarArchive/readEntryBytes */
  volume: number;
  /** byte offset within that volume */
  offset: number;
  length: number;
}

export interface RarEntry {
  /** archive-internal path, forward-slash separated */
  name: string;
  /** RAR method byte; only METHOD_STORE (0x30) can be streamed */
  method: number;
  /** total unpacked size (sum of chunk lengths, for stored entries) */
  size: number;
  encrypted: boolean;
  /** ordered byte ranges across volumes whose concatenation is this entry's data (stored entries only) */
  chunks: RarChunk[];
}

export interface RarArchive {
  entries: RarEntry[];
}

export function isStreamable(entry: RarEntry): boolean {
  return entry.method === METHOD_STORE && !entry.encrypted;
}

/** Reads `len` bytes starting at `pos` within one volume (0-based index into the volumes array). */
export type VolumeReadFn = (volume: number, pos: number, len: number) => Promise<Uint8Array>;

function u16(b: Uint8Array, o: number) {
  return b[o]! | (b[o + 1]! << 8);
}
function u32(b: Uint8Array, o: number) {
  return (b[o]! | (b[o + 1]! << 8) | (b[o + 2]! << 16) | (b[o + 3]! << 24)) >>> 0;
}

function decodeName(raw: Uint8Array, unicode: boolean): string {
  // Real-world scene filenames are ASCII; when LHD_UNICODE is set the field holds a NUL-terminated
  // ANSI name followed by an extra encoded-unicode section. We only need a usable display/compare
  // name, so take the ANSI half (stops at the first NUL) and otherwise decode the whole thing.
  if (unicode) {
    const nul = raw.indexOf(0);
    if (nul >= 0) return new TextDecoder().decode(raw.subarray(0, nul)).replace(/\\/g, "/");
  }
  return new TextDecoder().decode(raw).replace(/\\/g, "/");
}

/**
 * Parse the (small) block headers of a set of RAR volumes, in order, and return every stored
 * entry's chunk map. Never reads file *data* bytes (other than what a read of header-sized
 * regions happens to include) - only enough header bytes to find PACK_SIZE and jump to the next
 * block, so this works while only the front of each volume has downloaded.
 */
export interface ReadRarOptions {
  /**
   * Stop scanning as soon as a *complete* entry (one whose data doesn't continue into a further
   * volume) satisfies this predicate. Avoids ever having to read past a huge final entry's data -
   * which, for a torrent still downloading, may simply not exist yet - when the caller only cares
   * about one specific entry (the video). The returned archive may then be missing later entries.
   */
  stopWhen?: (entry: RarEntry) => boolean;
}

export async function readRarArchive(volumeLengths: number[], read: VolumeReadFn, opts: ReadRarOptions = {}): Promise<RarArchive> {
  const entries: RarEntry[] = [];
  let open: RarEntry | undefined; // entry whose last seen chunk had LHD_SPLIT_AFTER set

  for (let vol = 0; vol < volumeLengths.length; vol++) {
    const volLen = volumeLengths[vol]!;
    let pos = 0;

    const marker = await read(vol, 0, Math.min(8, volLen));
    if (startsWith(marker, MARKER_RAR5)) throw new RarUnsupportedError("RAR5 archives are not supported yet");
    if (!startsWith(marker, MARKER_OLD)) {
      throw new RarFormatError(`volume ${vol}: not a RAR archive (bad marker)`);
    }
    pos = MARKER_OLD.length;

    let sawEndArc = false;
    while (pos < volLen) {
      // common block header: HEAD_CRC(2) HEAD_TYPE(1) HEAD_FLAGS(2) HEAD_SIZE(2)
      const head = await read(vol, pos, 7);
      if (head.length < 7) break; // trailing padding shorter than a header: nothing more to parse
      const headType = head[2]!;
      const headFlags = u16(head, 3);
      const headSize = u16(head, 5);
      if (headSize < 7) throw new RarFormatError(`volume ${vol}: malformed block at ${pos} (head_size=${headSize})`);

      if (headType === HEAD_FILE || headType === HEAD_NEWSUB) {
        // fixed fields right after the 7-byte common header
        const FIXED = 4 + 4 + 1 + 4 + 4 + 1 + 1 + 2 + 4; // = 25
        const need = 7 + FIXED + (headFlags & LHD_LARGE ? 8 : 0);
        const buf = await read(vol, pos, need);
        if (buf.length < need) throw new RarFormatError(`volume ${vol}: truncated file header at ${pos}`);
        let o = 7;
        const packSizeLo = u32(buf, o);
        o += 4;
        const unpSizeLo = u32(buf, o);
        o += 4;
        o += 1; // host os
        o += 4; // file crc
        o += 4; // file time
        o += 1; // unpack version
        const method = buf[o]!;
        o += 1;
        const nameSize = u16(buf, o);
        o += 2;
        o += 4; // file attr
        let packHi = 0;
        let unpHi = 0;
        if (headFlags & LHD_LARGE) {
          packHi = u32(buf, o);
          o += 4;
          unpHi = u32(buf, o);
          o += 4;
        }
        const nameRaw = await read(vol, pos + o, nameSize);
        if (nameRaw.length < nameSize) throw new RarFormatError(`volume ${vol}: truncated file name at ${pos}`);
        const name = decodeName(nameRaw, !!(headFlags & LHD_UNICODE));

        const packSize = packSizeLo + packHi * 0x1_0000_0000;
        const isDir = (headFlags & LHD_WINDOWMASK) === LHD_WINDOW_DIR;
        const splitBefore = !!(headFlags & LHD_SPLIT_BEFORE);
        const splitAfter = !!(headFlags & LHD_SPLIT_AFTER);
        const encrypted = !!(headFlags & LHD_PASSWORD);
        const dataStart = pos + headSize;
        const chunk: RarChunk = { volume: vol, offset: dataStart, length: packSize };

        let current: RarEntry | undefined;
        if (!isDir) {
          if (splitBefore && open && (!name || open.name === name)) {
            current = open;
            current.chunks.push(chunk);
            current.size += packSize;
            if (encrypted) current.encrypted = true;
          } else {
            current = { name, method, size: packSize, encrypted, chunks: [chunk] };
            entries.push(current);
          }
        }
        open = !isDir && splitAfter ? current : undefined;
        if (!isDir && !splitAfter && current && opts.stopWhen?.(current)) return { entries };
        pos = dataStart + packSize;
        continue;
      }

      if (headType === HEAD_MARK) throw new RarFormatError(`volume ${vol}: unexpected marker block mid-stream`);

      // generic block (MAIN_HEAD, COMM_HEAD, AV/SIGN/PROTECT, ...): header carries its own size;
      // only a handful of legacy block types place extra data after it (flagged by LONG_BLOCK).
      let addSize = 0;
      if (headFlags & LONG_BLOCK) {
        const extra = await read(vol, pos + 7, 4);
        if (extra.length === 4) addSize = u32(extra, 0);
      }
      pos = pos + headSize + addSize;
      if (headType === HEAD_ENDARC) {
        sawEndArc = true;
        break;
      }
    }
    void sawEndArc;
  }

  return { entries };
}

function startsWith(buf: Uint8Array, prefix: Uint8Array) {
  if (buf.length < prefix.length) return false;
  for (let i = 0; i < prefix.length; i++) if (buf[i] !== prefix[i]) return false;
  return true;
}

/** Read `len` bytes at virtual offset `pos` of a (stored) entry's unpacked data. */
export async function readEntryBytes(entry: RarEntry, pos: number, len: number, read: VolumeReadFn): Promise<Uint8Array> {
  if (!isStreamable(entry)) {
    throw new RarUnsupportedError(
      entry.encrypted ? "RAR entry is password-protected" : `RAR entry uses compression method 0x${entry.method.toString(16)} (only stored/uncompressed entries can be streamed)`,
    );
  }
  const end = Math.min(entry.size, pos + len);
  if (end <= pos) return new Uint8Array(0);

  const out = new Uint8Array(end - pos);
  let outOff = 0;
  let base = 0; // cumulative virtual offset at the start of the current chunk
  for (const c of entry.chunks) {
    const chunkStart = base;
    const chunkEnd = base + c.length;
    base = chunkEnd;
    if (chunkEnd <= pos || chunkStart >= end) continue;
    const readStart = Math.max(pos, chunkStart);
    const readEnd = Math.min(end, chunkEnd);
    const bytes = await read(c.volume, c.offset + (readStart - chunkStart), readEnd - readStart);
    out.set(bytes.subarray(0, readEnd - readStart), outOff);
    outOff += readEnd - readStart;
    if (chunkEnd >= end) break;
  }
  return outOff === out.length ? out : out.subarray(0, outOff);
}

// ---- volume set grouping (filename conventions, no I/O) ----

export interface VolumeGroup<F> {
  /** lowercase group key (directory + base name, without the volume-specific suffix) */
  key: string;
  /** volumes in archive order (first volume first) */
  volumes: F[];
}

const NEW_STYLE = /^(.*)\.part(\d+)\.rar$/i;
const OLD_STYLE_BASE = /^(.*)\.rar$/i;
const OLD_STYLE_VOL = /^(.*)\.r(\d{2,3})$/i;

/** Group a torrent's (or any) file list into RAR multi-volume sets, ordered for playback/parsing. */
export function groupRarVolumes<F extends { path: string }>(files: F[]): VolumeGroup<F>[] {
  const newStyle = new Map<string, Map<number, F>>();
  const oldBase = new Map<string, F>();
  const oldVols = new Map<string, Map<number, F>>();

  for (const f of files) {
    let m: RegExpExecArray | null;
    if ((m = NEW_STYLE.exec(f.path))) {
      const key = m[1]!.toLowerCase();
      if (!newStyle.has(key)) newStyle.set(key, new Map());
      newStyle.get(key)!.set(parseInt(m[2]!, 10), f);
      continue;
    }
    if ((m = OLD_STYLE_VOL.exec(f.path))) {
      const key = m[1]!.toLowerCase();
      if (!oldVols.has(key)) oldVols.set(key, new Map());
      oldVols.get(key)!.set(parseInt(m[2]!, 10), f);
      continue;
    }
    if ((m = OLD_STYLE_BASE.exec(f.path))) {
      oldBase.set(m[1]!.toLowerCase(), f);
      continue;
    }
  }

  const groups: VolumeGroup<F>[] = [];
  for (const [key, vols] of newStyle) {
    const nums = [...vols.keys()].sort((a, b) => a - b);
    groups.push({ key, volumes: nums.map((n) => vols.get(n)!) });
  }
  for (const [key, base] of oldBase) {
    const vols = oldVols.get(key);
    const volumes = [base];
    if (vols) for (const n of [...vols.keys()].sort((a, b) => a - b)) volumes.push(vols.get(n)!);
    groups.push({ key, volumes });
  }
  return groups;
}

/** True if this path looks like it's part of a (possibly single-volume) RAR set. */
export const RAR_VOLUME = /\.(rar|r\d{2,3})$/i;
